import React, { useState, useEffect, useMemo } from 'react';
import { X, ClipboardList, Download } from 'lucide-react';
import { api } from '../api';

const card = {
  background: 'var(--white)', borderRadius: 12,
  border: '1px solid var(--border)', padding: '20px 24px',
};

const typeConfig = {
  success: { bg: 'var(--green-light)', color: 'var(--green)', dot: '#16a34a', label: 'Entrée / Sortie' },
  warning: { bg: 'var(--amber-light)', color: 'var(--amber)', dot: '#d97706', label: 'Accès bloqué' },
  error:   { bg: 'var(--red-light)',   color: 'var(--red)',   dot: '#dc2626', label: 'Accès refusé' },
};

function exportCSV(logs) {
  const header = 'Date,Heure,Employé,Zone,Action,Type';
  const rows = logs.map(l => {
    const d = new Date(l.created_at);
    return `${d.toLocaleDateString('fr-FR')},${d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' })},"${l.employe_nom || '—'}","${l.porte_nom || '—'}","${l.action}",${l.type}`;
  });
  const blob = new Blob([[header, ...rows].join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = `qbdp-historique-${new Date().toISOString().slice(0,10)}.csv`; a.click();
  URL.revokeObjectURL(url);
}

export default function Historique() {
  const [logs, setLogs]         = useState([]);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState('');
  const [filterType, setFilterType] = useState('tous');
  const [filterDate, setFilterDate] = useState('');
  const [filterZone, setFilterZone] = useState('toutes');
  const [page, setPage]         = useState(1);
  const PER_PAGE = 15;

  useEffect(() => {
    api.getLogs({ limit: 500 }).then(setLogs).catch(console.error).finally(() => setLoading(false));
  }, []);

  const zones = useMemo(() => ['toutes', ...Array.from(new Set(logs.map(l => l.porte_nom).filter(Boolean)))], [logs]);

  const filtered = useMemo(() => {
    return logs.filter(l => {
      const d = l.created_at ? new Date(l.created_at).toISOString().slice(0,10) : '';
      const matchSearch = search === '' ||
        (l.employe_nom || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.porte_nom || '').toLowerCase().includes(search.toLowerCase()) ||
        (l.action || '').toLowerCase().includes(search.toLowerCase());
      const matchType = filterType === 'tous' || l.type === filterType;
      const matchDate = filterDate === '' || d === filterDate;
      const matchZone = filterZone === 'toutes' || l.porte_nom === filterZone;
      return matchSearch && matchType && matchDate && matchZone;
    });
  }, [logs, search, filterType, filterDate, filterZone]);

  const totalPages = Math.ceil(filtered.length / PER_PAGE);
  const paginated  = filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE);

  const resetFilters = () => {
    setSearch(''); setFilterType('tous'); setFilterDate(''); setFilterZone('toutes'); setPage(1);
  };

  const countSuccess = logs.filter(l => l.type === 'success').length;
  const countWarning = logs.filter(l => l.type === 'warning').length;
  const countError   = logs.filter(l => l.type === 'error').length;

  const inp = { border: '1px solid var(--border)', borderRadius: 8, padding: '8px 12px', fontSize: 13, outline: 'none', background: 'white' };

  if (loading) return <div style={{ padding: '28px 32px', color: 'var(--gray)', fontSize: 13 }}>Chargement...</div>;

  return (
    <div style={{ padding: '28px 32px' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24 }}>
        <div>
          <h1 style={{ fontSize: 20, fontWeight: 600 }}>Historique</h1>
          <p style={{ fontSize: 13, color: 'var(--gray)', marginTop: 4 }}>{logs.length} événements enregistrés</p>
        </div>
        <button onClick={() => exportCSV(filtered)} style={{ background: 'var(--blue)', color: 'white', border: 'none', borderRadius: 8, padding: '9px 18px', fontSize: 13, fontWeight: 500, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 6 }}>
          <Download size={14} /> Exporter CSV ({filtered.length})
        </button>
      </div>

      <div style={{ display: 'flex', gap: 14, marginBottom: 24 }}>
        {[
          { val: logs.length,   lbl: 'Total événements', color: '#1a1a2e' },
          { val: countSuccess,  lbl: 'Entrées / Sorties', color: '#00B050' },
          { val: countWarning,  lbl: 'Accès bloqués',    color: '#ef4444' },
          { val: countError,    lbl: 'Accès refusés',    color: '#ef4444' },
        ].map(m => (
          <div key={m.lbl} style={{ ...card, flex: 1, padding: '14px 18px' }}>
            <div style={{ fontSize: 24, fontWeight: 600, color: m.color }}>{m.val}</div>
            <div style={{ fontSize: 11, color: m.color, marginTop: 3 }}>{m.lbl}</div>
          </div>
        ))}
      </div>

      <div style={{ ...card, marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center' }}>
          <input style={{ ...inp, width: 220 }} placeholder="Rechercher…" value={search}
            onChange={e => { setSearch(e.target.value); setPage(1); }} />
          <select style={inp} value={filterType} onChange={e => { setFilterType(e.target.value); setPage(1); }}>
            <option value="tous">Tous les types</option>
            <option value="success">Entrée / Sortie</option>
            <option value="warning">Accès bloqué</option>
            <option value="error">Accès refusé</option>
          </select>
          <select style={{ ...inp, maxWidth: 220 }} value={filterZone} onChange={e => { setFilterZone(e.target.value); setPage(1); }}>
            {zones.map(z => <option key={z} value={z}>{z === 'toutes' ? 'Toutes les zones' : z}</option>)}
          </select>
          <input style={inp} type="date" value={filterDate} onChange={e => { setFilterDate(e.target.value); setPage(1); }} />
          {(search || filterType !== 'tous' || filterDate || filterZone !== 'toutes') && (
            <button onClick={resetFilters} style={{ border: '1px solid var(--border)', background: 'white', borderRadius: 8, padding: '8px 14px', fontSize: 12, color: 'var(--gray)', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 5 }}><X size={12} /> Réinitialiser</button>
          )}
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--gray)' }}>{filtered.length} résultat{filtered.length !== 1 ? 's' : ''}</span>
        </div>
      </div>

      <div style={card}>
        {logs.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '48px 0', color: 'var(--gray)', fontSize: 13 }}>
            <div style={{ marginBottom: 12, display: 'flex', justifyContent: 'center' }}><ClipboardList size={32} color="#9ca3af" /></div>
            Aucun événement enregistré pour le moment.<br/>
            <span style={{ fontSize: 12 }}>Les accès des employés apparaîtront ici.</span>
          </div>
        ) : (
          <>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['', 'Date & heure', 'Employé', 'Zone', 'Action', 'Type'].map(h => (
                    <th key={h} style={{ textAlign: 'left', padding: '8px 10px 10px', fontSize: 11, color: 'var(--gray)', fontWeight: 500 }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginated.map((log, i) => {
                  const cfg = typeConfig[log.type] || typeConfig.success;
                  const d = log.created_at ? new Date(log.created_at) : null;
                  const nom = log.employe_nom || '—';
                  return (
                    <tr key={i} style={{ borderBottom: '1px solid #f9fafb' }}>
                      <td style={{ padding: '11px 10px', width: 12 }}>
                        <div style={{ width: 8, height: 8, borderRadius: '50%', background: cfg.dot }} />
                      </td>
                      <td style={{ padding: '11px 10px', fontSize: 12, color: '#374151', whiteSpace: 'nowrap' }}>
                        <div style={{ fontWeight: 500 }}>{d ? d.toLocaleDateString('fr-FR') : '—'}</div>
                        <div style={{ color: 'var(--gray)', marginTop: 1 }}>{d ? d.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit' }) : '—'}</div>
                      </td>
                      <td style={{ padding: '11px 10px', fontSize: 13 }}>
                        {nom !== '—' ? (
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'var(--blue-light)', color: 'var(--blue)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 9, fontWeight: 600, flexShrink: 0 }}>
                              {nom.split(' ').map(w => w[0]).join('').slice(0, 2).toUpperCase()}
                            </div>
                            {nom}
                          </div>
                        ) : <span style={{ color: 'var(--gray)', fontStyle: 'italic' }}>Inconnu</span>}
                      </td>
                      <td style={{ padding: '11px 10px', fontSize: 12, color: '#374151' }}>{log.porte_nom || '—'}</td>
                      <td style={{ padding: '11px 10px', fontSize: 12 }}>{log.action || '—'}</td>
                      <td style={{ padding: '11px 10px' }}>
                        <span style={{ background: cfg.bg, color: cfg.color, fontSize: 10, fontWeight: 600, padding: '3px 8px', borderRadius: 10 }}>{cfg.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            {paginated.length === 0 && (
              <div style={{ textAlign: 'center', padding: '32px 0', color: 'var(--gray)', fontSize: 13 }}>Aucun événement trouvé</div>
            )}
            {totalPages > 1 && (
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: 16, paddingTop: 14, borderTop: '1px solid var(--border)' }}>
                <span style={{ fontSize: 12, color: 'var(--gray)' }}>Page {page} sur {totalPages}</span>
                <div style={{ display: 'flex', gap: 6 }}>
                  <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                    style={{ border: '1px solid var(--border)', background: 'white', borderRadius: 7, padding: '5px 14px', fontSize: 12, cursor: page === 1 ? 'default' : 'pointer', opacity: page === 1 ? 0.4 : 1 }}>← Précédent</button>
                  {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => i + 1).map(n => (
                    <button key={n} onClick={() => setPage(n)} style={{
                      border: '1px solid', borderRadius: 7, padding: '5px 10px', fontSize: 12, cursor: 'pointer',
                      borderColor: n === page ? 'var(--blue)' : 'var(--border)',
                      background: n === page ? 'var(--blue-light)' : 'white',
                      color: n === page ? 'var(--blue)' : '#374151',
                      fontWeight: n === page ? 500 : 400,
                    }}>{n}</button>
                  ))}
                  <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                    style={{ border: '1px solid var(--border)', background: 'white', borderRadius: 7, padding: '5px 14px', fontSize: 12, cursor: page === totalPages ? 'default' : 'pointer', opacity: page === totalPages ? 0.4 : 1 }}>Suivant →</button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
